import { useState } from 'react'
import { Loader2, Shield } from 'lucide-react'
import type { UserWithPermissions } from '@portal/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { usersApi } from '@/lib/api/users'
import { cn } from '@/lib/utils'

// DASHBOARD is always-on — not shown here. EMPLOYEES deferred to a future build.
const TOGGLEABLE_MODULES: { key: string; label: string; description: string }[] = [
  { key: 'PAYMENTS',  label: 'Payments',         description: 'Create and submit payment requests' },
  { key: 'APPROVALS', label: 'Approvals',         description: 'Approve or reject pending payments' },
  { key: 'VENDORS',   label: 'Vendors',           description: 'Manage vendor accounts and bene registration' },
  { key: 'USERS',     label: 'User Management',   description: 'Add, edit, and manage platform users' },
]

interface Props {
  open: boolean
  user: UserWithPermissions
  onClose: () => void
  onSaved: (user: UserWithPermissions) => void
}

export default function PermissionMatrix({ open, user, onClose, onSaved }: Props) {
  const [granted, setGranted] = useState<Set<string>>(new Set(user.modules))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const toggle = (module: string) => {
    setGranted((prev) => {
      const next = new Set(prev)
      if (next.has(module)) next.delete(module)
      else next.add(module)
      return next
    })
  }

  const handleSave = async () => {
    setError('')
    setLoading(true)
    try {
      const permissions = TOGGLEABLE_MODULES.map(({ key }) => ({
        module: key,
        granted: granted.has(key),
      }))
      const res = await usersApi.updatePermissions(user.id, permissions)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to save permissions')
      onSaved(res.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Module Permissions — {user.name}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-2">
          Changes take effect on the user's next API request.
        </p>

        <div className="space-y-2">
          {TOGGLEABLE_MODULES.map(({ key, label, description }) => {
            const isGranted = granted.has(key)
            return (
              <div
                key={key}
                onClick={() => toggle(key)}
                className={cn(
                  'flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-accent',
                  isGranted ? 'border-primary/30 bg-primary/5' : 'border-border',
                )}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <div
                  className={cn(
                    'ml-4 flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                    isGranted ? 'bg-primary' : 'bg-muted',
                  )}
                >
                  <div
                    className={cn(
                      'h-4 w-4 rounded-full bg-white shadow transition-transform mx-0.5',
                      isGranted ? 'translate-x-4' : 'translate-x-0',
                    )}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Permissions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
